import classNames from 'classnames';
import { ColumnDef, flexRender, SortingState } from '@tanstack/react-table';
import SortIcon from '@components/primitives/dataGrid/components/head/SortIcon';
import { purifySorting } from '@components/primitives/dataGrid/utils/purifyDataGridState';
import { ColumnFiltersState, ColumnFilterValue, DataGridRowProps, NATIVE_COLUMNS } from '@components/primitives/dataGrid/DataGrid';
import { Table } from '@tanstack/table-core';
import { DynamicField } from '@models/DynamicField';
import { Dispatch, ReactNode } from 'react';
import { SetStateAction } from 'jotai';
import Filter from '@components/primitives/dataGrid/components/filters/Filter';
import DataGridOptions from '@components/primitives/dataGrid/components/DataGridOptions';
import { NewNumericFilterValue } from '@components/primitives/dataGrid/components/filters/NumericFilter';
import useDataGridColumnAutoSizer from '@components/primitives/dataGrid/hooks/useDataGridColumnAutoSizer';

interface Props<TData> {
    // eslint-disable-next-line
    columns: ColumnDef<TData, any>[];
    table: Table<TData>;
    storageKey?: string;
    getHeaderComponent?: (column?: DynamicField) => ReactNode;
    resetSorting: () => void;
    resetColumnFilters: () => void;
    disableSettings?: boolean;
    purifiedColumnFilters: ColumnFiltersState;
    showFilters: boolean;
    setShowFilters: Dispatch<SetStateAction<boolean>>;
    sorting: SortingState;
    onOpenGridSettings: () => void;
    name: string;
}

const DataGridHead = <TData extends DataGridRowProps>({
    columns,
    table,
    storageKey,
    getHeaderComponent,
    resetSorting,
    resetColumnFilters,
    disableSettings,
    purifiedColumnFilters,
    showFilters,
    setShowFilters,
    sorting,
    onOpenGridSettings,
    name,
}: Props<TData>) => {
    const { autoSizeColumn } = useDataGridColumnAutoSizer();
    const sortingAvailable = columns.filter((column) => !!column.sortingFn).length > 0;
    const filteringAvailable = columns.filter((column) => !!column.filterFn && column.enableColumnFilter !== false).length > 0;

    function isNumericFilterValue(filterValue: ColumnFilterValue): filterValue is NewNumericFilterValue {
        return (filterValue as NewNumericFilterValue)?.isRangeMode !== undefined;
    }

    const columnFilters = purifiedColumnFilters?.filter((filter) => {
        if (isNumericFilterValue(filter.value)) {
            return !!filter.value?.min || !!filter.value?.min;
        }

        return true;
    });

    const onToggleFilters = () => {
        setShowFilters((value) => !value);
    };

    const onResizerDoubleClick = (columnId: string) => {
        autoSizeColumn(table, columnId);
    };

    return (
        <div className="dataGrid__head">
            <DataGridOptions<TData>
                columns={columns}
                table={table}
                storageKey={storageKey}
                csvFileName={`${name}_export.csv`}
                onOpenGridSettings={onOpenGridSettings}
                gridSettingsEnabled={!!storageKey && !disableSettings}
                onResetFilters={resetColumnFilters}
                resetFiltersVisible={filteringAvailable}
                resetFiltersEnabled={!!columnFilters?.length}
                onResetSorting={resetSorting}
                resetSortingEnabled={!!sorting?.length}
                resetSortingVisible={sortingAvailable}
                toggleFiltersVisible={filteringAvailable}
                showFilters={showFilters}
                onToggleShowFilters={onToggleFilters}
            />
            {table.getHeaderGroups().map((headerGroup) => (
                <div key={headerGroup.id} className="dataGrid__tr">
                    {headerGroup.headers.map((header) => (
                        <div
                            key={header.id}
                            className={classNames('dataGrid__head-cell', {
                                'dataGrid__head-cell--sorted': header.column.getIsSorted(),
                            })}
                            style={{
                                width: header.getSize(),
                            }}
                        >
                            <div className={`dataGrid__label ${header.column.getCanSort() ? 'dataGrid__label--canSort' : ''}`} onClick={header.column.getToggleSortingHandler()}>
                                {header.isPlaceholder ? null : <span data-measure-head={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</span>}
                                {getHeaderComponent && !NATIVE_COLUMNS.includes(header.id) ? (
                                    <span data-measure-head-component={header.id}>{getHeaderComponent(header.column.columnDef.meta?.fieldDefinition)}</span>
                                ) : null}
                            </div>
                            {header.column.getCanFilter() ? (
                                <div>
                                    <Filter<TData> column={header.column} />
                                </div>
                            ) : null}
                            {header.column.getIsSorted() ? (
                                <SortIcon id={header.column.id} isDesc={header.column.getIsSorted() === 'desc'} sorting={purifySorting(sorting, columns)} />
                            ) : null}
                            <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onDoubleClick={() => onResizerDoubleClick(header.id)}
                                className={classNames('dataGrid__resizer', {
                                    'dataGrid__resizer--isResizing': header.column.getIsResizing(),
                                })}
                            />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default DataGridHead;
